// LedgerTableCombined.jsx
import { useMemo, useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { getAmountInCurrencyFormat, limitNumber } from "../../utils/number.js";
import ModalWrapper from "../common/ModalWrapper.jsx";
import {
  accumulateTotals,
  getTransactionById,
  initTotals,
  subtractObjects,
} from "../../utils/report.js";
import { TransactionUpdate } from "../salepoint/TransactionUpdate.jsx";
import {
  deleteTransactionBySection,
  revertTransactionBySection,
} from "../../services/transactionService.js";
import { rowPassesFilter, chargeStatus } from "./FilterOptions.js";
import LedgerStats, { LedgerStatsComputed } from "./LedgerStats.jsx";
import TransactionAccordionModal from "./TransactionAccordionModal.jsx";
import FilterURL from "../filters/FilterUrl.jsx";

const ALL_COLUMNS = [
  { key: "tx", label: "TX", always: true },
  { key: "serial", label: "S.No", always: true },
  { key: "date", label: "Date", always: true },
  { key: "party", label: "Party", always: true },
  { key: "cash", label: "Cash", group: "cash" },
  { key: "pc", label: "PC (g)", group: "pc" },
  { key: "lab_weight", label: "Lab Wt", group: "lab" },
  // { key: "lab_report", label: "Lab Rpt", group: "lab" },
  { key: "bar", label: "Bar (g)", group: "bar" },
  { key: "coin", label: "Coin (g)", group: "coin" },
  { key: "coin_karat", label: "Karat", group: "coin" },
  { key: "ginni", label: "Ginni (g)", group: "ginni" },
  // { key: "ginni_report", label: "Ginni Rpt", group: "ginni" },
  { key: "silver_weight", label: "Silver Wt", group: "silver" },
  // { key: "silver_bar", label: "Silver Bar", group: "silver" },
  // { key: "silver_amount", label: "Silver Amt", group: "silver" },
  { key: "rwg_weight", label: "RWG Wt", group: "rwg" },
  { key: "rwg_rate", label: "RWG Rate", group: "rwg" },
  { key: "rwg_amount", label: "RWG Amt", group: "rwg" },
  { key: "rws_weight", label: "RWS Wt", group: "rws" },
  { key: "rws_rate", label: "RWS Rate", group: "rws" },
  { key: "rws_amount", label: "RWS Amt", group: "rws" },
];

function getActiveGroups(rows) {
  const groups = new Set();
  rows.forEach((row) => {
    if (row.cash > 0) groups.add("cash");
    if (row.pc?.weight > 0) groups.add("pc");
    if (row.lab?.weight > 0) groups.add("lab");
    if (row.bar?.weight > 0) groups.add("bar");
    if (row.coin?.weight > 0) groups.add("coin");
    if (row.ginni?.weight > 0) groups.add("ginni");
    if (row.silver?.weight > 0 || row.silver?.silverBar > 0)
      groups.add("silver");
    if (row.rwg?.weight > 0) groups.add("rwg");
    if (row.rws?.weight > 0) groups.add("rws");
  });
  return groups;
}

export default function LedgerTableCombined({
  receiveRows = [],
  givenRows = [],
  givenTotals,
  receiveTotals,
  balance,
  stats = {},
  loading,
  editMode,
  originalData,
  refresh,
  isDailyReport = false,
  setFilters,
}) {
  const [editTx, setEditTx] = useState(null);
  const [editType, setEditType] = useState(null);
  const [activeFilters, setActiveFilters] = useState(new Set());
  const [viewTx, setViewTx] = useState(null);

  const maxLen = Math.max(receiveRows.length, givenRows.length);
  const pairedRows = Array.from({ length: maxLen }, (_, i) => ({
    rx: receiveRows[i] || null,
    gv: givenRows[i] || null,
  }));

  const activeGroups = useMemo(
    () => getActiveGroups([...receiveRows, ...givenRows]),
    [receiveRows, givenRows],
  );
  const visibleColumns = ALL_COLUMNS.filter(
    (col) => col.always || activeGroups.has(col.group),
  );

  const handleEdit = (transactionID, type) => {
    setEditTx(getTransactionById(originalData, transactionID));
    setEditType(type);
  };

  const handleView = ({ txId, type }) => {
    const tx = getTransactionById(originalData, txId);
    setViewTx({ tx, type });
  };

  const handleRemove = async (transactionID, sectionType) => {
    try {
      const res = await Swal.fire({
        icon: "warning",
        title: "Are you sure?",
        text: "You are about to delete this transaction. This action cannot be undone.",
        showCancelButton: true,
        confirmButtonText: "Yes, Delete",
        cancelButtonText: "Cancel",
      });
      if (res.isConfirmed) {
        const response = await deleteTransactionBySection(
          transactionID,
          sectionType,
        );
        if (response.success) {
          await Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: "Transaction deleted successfully.",
          });
          refresh();
        } else {
          await Swal.fire({
            icon: "error",
            title: "Error",
            text: "Unknown error while deleting.",
          });
        }
      }
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Something went wrong.",
      });
    }
  };

  const handleRevert = async (transactionID, sectionType) => {
    try {
      const res = await Swal.fire({
        icon: "warning",
        title: "Are you sure?",
        text: `This will revert the ${sectionType} transaction`,
        showCancelButton: true,
        confirmButtonText: "Yes, Revert",
        cancelButtonText: "Cancel",
      });
      if (res.isConfirmed) {
        const response = await revertTransactionBySection(
          transactionID,
          sectionType,
        );
        if (response.success) {
          await Swal.fire({
            icon: "success",
            title: "Reverted!",
            text: "Transaction reverted successfully.",
          });
          refresh();
        } else {
          await Swal.fire({
            icon: "error",
            title: "Error",
            text: "Unknown error while reverting.",
          });
        }
      }
    } catch {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Something went wrong.",
      });
    }
  };

  // console.log({ totalAndBalanceStats });
  return (
    <>
      <div style={{ marginBottom: "100px" }}>
        <div className="ledger-unified-wrap">
          <FilterURL onChange={setFilters} />

          <div className="lt2-wrap">
            <div className="lt2-scroll">
              {loading ? (
                <div className="lt2-state">
                  <div className="lt2-spinner" />
                  <span>Loading transactions…</span>
                </div>
              ) : pairedRows.length === 0 ? (
                <div className="lt2-state lt2-empty">
                  <i className="fas fa-inbox" />
                  <span>No transactions found</span>
                </div>
              ) : (
                <table className="lt2-table">
                  <thead>
                    <tr className="lt2-head-group">
                      {editMode && (
                        <th rowSpan={2} className="lt2-th-actions">
                          Actions
                        </th>
                      )}
                      <th
                        colSpan={visibleColumns.length}
                        className="lt2-section-head lt2-head-rx"
                      >
                        <i className="fas fa-arrow-down" /> Receive
                        <span className="lt2-badge">{receiveRows.length}</span>
                      </th>
                      <th className="lt2-divider-col" rowSpan={2} />
                      <th
                        colSpan={visibleColumns.length}
                        className="lt2-section-head lt2-head-gv"
                      >
                        <i className="fas fa-arrow-up" /> Given
                        <span className="lt2-badge">{givenRows.length}</span>
                      </th>
                      {editMode && (
                        <th rowSpan={2} className="lt2-th-actions">
                          Actions
                        </th>
                      )}
                    </tr>
                    <tr className="lt2-head-cols">
                      {visibleColumns.map((col) => (
                        <th key={`rx-${col.key}`} className="lt2-th">
                          {col.label}
                        </th>
                      ))}
                      {visibleColumns.map((col) => (
                        <th key={`gv-${col.key}`} className="lt2-th">
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pairedRows.map(({ rx, gv }, i) => (
                      <tr key={i} className="lt2-row">
                        {editMode && (
                          <td className="lt2-td-actions">
                            {rx && (
                              <ActionButtons
                                onEdit={() => handleEdit(rx.txId, "receive")}
                                onRemove={() =>
                                  handleRemove(rx.txId, "receive")
                                }
                                onRevert={() =>
                                  handleRevert(rx.txId, "receive")
                                }
                              />
                            )}
                          </td>
                        )}
                        {rx ? (
                          visibleColumns.map((col) => (
                            <td
                              key={`rx-${col.key}`}
                              className="lt2-td lt2-td-rx"
                            >
                              <CellValue
                                col={col}
                                row={rx}
                                handleView={(txId) =>
                                  handleView({ txId, type: "receive" })
                                }
                              />
                            </td>
                          ))
                        ) : (
                          <td
                            colSpan={visibleColumns.length}
                            className="lt2-td lt2-td-empty"
                          >
                            —
                          </td>
                        )}
                        <td className="lt2-divider-col" />
                        {gv ? (
                          visibleColumns.map((col) => (
                            <td
                              key={`gv-${col.key}`}
                              className="lt2-td lt2-td-gv"
                            >
                              <CellValue
                                col={col}
                                row={gv}
                                handleView={(txId) =>
                                  handleView({ txId, type: "given" })
                                }
                              />
                            </td>
                          ))
                        ) : (
                          <td
                            colSpan={visibleColumns.length}
                            className="lt2-td lt2-td-empty"
                          >
                            —
                          </td>
                        )}
                        {editMode && (
                          <td className="lt2-td-actions">
                            {gv && (
                              <ActionButtons
                                onEdit={() => handleEdit(gv.txId, "given")}
                                onRemove={() => handleRemove(gv.txId, "given")}
                                onRevert={() => handleRevert(gv.txId, "given")}
                              />
                            )}
                          </td>
                        )}
                      </tr>
                    ))}

                    <TotalsRow
                      label="Totals"
                      rxTotals={receiveTotals}
                      gvTotals={givenTotals}
                      visibleColumns={visibleColumns}
                      editMode={editMode}
                      rowClass="lt2-total-row"
                    />
                    <BalanceRow
                      balance={balance}
                      visibleColumns={visibleColumns}
                      editMode={editMode}
                    />
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
        {/* Stats */}
        {true && (
          <>
            <LedgerStatsComputed stats={stats} isDailyReport={true} />
            {/* <LedgerStats
              receiveRows={receiveRows}
              givenRows={givenRows}
              isDailyReport={isDailyReport}
            /> */}
          </>
        )}

        <ModalWrapper
          show={!!editTx}
          onClose={() => {
            setEditTx(null);
            setEditType(null);
          }}
          title="Edit Transaction"
          size="lg"
        >
          <div className="section-sp">
            <TransactionUpdate
              type={editType}
              initialTx={editTx}
              seletectedCustomer={{
                customer: {
                  customerId: editTx?.customerId?._id || "",
                  customerName: editTx?.customerId?.name || "",
                  customerAddress: editTx?.customerId?.adress || "",
                  customerContact: editTx?.customerId?.phone,
                },
                serialNumber: editTx?.serialNo,
                reportDate: editTx?.transactionDate,
              }}
              onUpdate={() => {
                refresh();
                setEditTx(null);
                setEditType(null);
              }}
            />
          </div>
        </ModalWrapper>
      </div>
      <TransactionAccordionModal
        viewTx={viewTx}
        onClose={() => setViewTx(null)}
      />
    </>
  );
}

/* ─────────────────────────────────────────
   ActionButtons
───────────────────────────────────────── */
function ActionButtons({ onEdit, onRemove, onRevert }) {
  return (
    <div className="lu-actions">
      <button className="lu-btn lu-btn-edit" title="Edit" onClick={onEdit}>
        {" "}
        <i className="fas fa-pen fa-sm" />
      </button>
      {/* <button
        className="lu-btn lu-btn-remove"
        title="Remove"
        onClick={onRemove}
      >
        <i className="fas fa-trash fa-sm" />
      </button> */}
      <button
        className="lu-btn lu-btn-revert"
        title="Revert"
        onClick={onRevert}
      >
        <i className="fas fa-rotate-left fa-sm" />
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────
   CellValue
───────────────────────────────────────── */
function CellValue({ col, row, handleView, isSummaryRow = false }) {
  switch (col.key) {
    case "tx":
      return (
        <button
          className="lu-btn lu-btn-show"
          title="Show Tx"
          onClick={() => handleView(row.txId)}
        >
          <i className="fas fa-eye fa-sm" />
        </button>
      );
    case "serial":
      return <span className="lt2-serial">{row.serial}</span>;
    case "date":
      return row.date ? new Date(row.date).toLocaleDateString() : "—";
    case "party":
      return <span className="lt2-party">{row.party || "—"}</span>;
    case "cash":
      return getAmountInCurrencyFormat(showVal(row.cash));
    case "pc":
      return (
        <SectionCell
          label="PC"
          weight={row.pc?.weight}
          amount={row.pc?.charges}
          paidAmount={row.pc?.paidAmount}
          isSummaryRow={isSummaryRow}
        />
      );
    case "lab_weight":
      return (
        <SectionCell
          label="Lab"
          weight={row.lab?.weight}
          totalWeight={row.lab?.totalWeight}
          amount={row.lab?.charges}
          paidAmount={row.lab?.paidAmount}
          isSummaryRow={isSummaryRow}
          labelForWeight="Pure Weight"
        />
      );
    case "lab_report":
      return (
        <span>
          {isReportMissing(row.lab?.weight, row.lab?.report) ? (
            <span className="lt2-missing" title="Lab report missing">
              ⚠
            </span>
          ) : (
            showVal(row.lab?.report)
          )}
        </span>
      );
    case "bar":
      return (
        <SectionCell
          label="Bar"
          weight={row.bar?.weight}
          amount={row.bar?.charges}
          paidAmount={row.bar?.paidAmount}
          isSummaryRow={isSummaryRow}
        />
      );
    case "coin":
      return (
        <SectionCell
          label="Coin"
          weight={row.coin?.weight}
          amount={row.coin?.charges}
          paidAmount={row.coin?.paidAmount}
          isSummaryRow={isSummaryRow}
        />
      );
    case "coin_karat":
      return showVal(row.coin?.karat);
    case "ginni":
      return (
        <SectionCell
          label="Ginni"
          weight={row.ginni?.weight}
          totalWeight={row.ginni?.totalWeight}
          amount={row.ginni?.charges}
          paidAmount={row.ginni?.paidAmount}
          isSummaryRow={isSummaryRow}
          labelForWeight="Pure Weight"
        />
      );
    case "ginni_report":
      return (
        <span>
          {isReportMissing(row.ginni?.weight, row.ginni?.report) ? (
            <span className="lt2-missing" title="Ginni report missing">
              ⚠
            </span>
          ) : (
            showVal(row.ginni?.report)
          )}
        </span>
      );
    case "silver_weight":
      return (
        <SectionCell
          label="Silver"
          weight={row.silver?.weight}
          amount={row.silver?.charges}
          paidAmount={row.silver?.paidAmount}
          isSummaryRow={isSummaryRow}
        />
      );
    case "silver_bar":
      return showVal(row.silver?.silverBar);
    case "silver_amount":
      return showVal(row.silver?.charges);
    case "rwg_weight":
      return showVal(row.rwg?.weight);
    case "rwg_rate":
      return showVal(row.rwg?.rate);
    case "rwg_amount":
      return showVal(row.rwg?.amount);
    case "rws_weight":
      return showVal(row.rws?.weight);
    case "rws_rate":
      return showVal(row.rws?.rate);
    case "rws_amount":
      return showVal(row.rws?.amount);
    default:
      return "—";
  }
}

/* ─────────────────────────────────────────
   TotalsRow
───────────────────────────────────────── */
function TotalsRow({
  label,
  rxTotals,
  gvTotals,
  visibleColumns,
  editMode,
  rowClass,
}) {
  const cell = (totals, colKey) => {
    switch (colKey) {
      case "serial":
        return label;
      case "date":
        return "";
      case "party":
        return "";
      case "cash":
        return getAmountInCurrencyFormat(showVal(totals?.cash));
      case "pc":
        return (
          <SectionCell
            label="PC"
            weight={totals?.pc?.weight}
            amount={totals?.pc?.charges}
            paidAmount={totals?.pc?.paidAmount}
          />
        );
      case "lab_weight":
        return (
          <SectionCell
            label="Lab"
            weight={totals?.lab?.weight}
            totalWeight={totals?.lab?.totalWeight}
            amount={totals?.lab?.charges}
            paidAmount={totals?.lab?.paidAmount}
            labelForWeight="Pure Weight"
          />
        );
      case "lab_report":
        return "—";
      case "bar":
        return (
          <SectionCell
            label="Bar"
            weight={totals?.bar?.weight}
            amount={totals?.bar?.charges}
            paidAmount={totals?.bar?.paidAmount}
          />
        );
      case "coin":
        return (
          <SectionCell
            label="Coin"
            weight={totals?.coin?.weight}
            amount={totals?.coin?.charges}
            paidAmount={totals?.coin?.paidAmount}
          />
        );
      case "coin_karat":
        return "—";
      case "ginni":
        return (
          <SectionCell
            label="Ginni"
            weight={totals?.ginni?.weight}
            totalWeight={totals?.ginni?.totalWeight}
            amount={totals?.ginni?.charges}
            paidAmount={totals?.ginni?.paidAmount}
            labelForWeight="Pure Weight"
          />
        );
      case "ginni_report":
        return "—";
      case "silver_weight":
        return (
          <SectionCell
            label="Silver"
            weight={totals?.silver?.weight}
            amount={totals?.silver?.charges}
            paidAmount={totals?.silver?.paidAmount}
          />
        );
      case "silver_bar":
        return showVal(totals?.silver?.silverBar);
      case "silver_amount":
        return showVal(totals?.silver?.charges);
      case "rwg_weight":
        return showVal(totals?.rwg?.weight);
      case "rwg_rate":
        return showVal(totals?.rwg?.rate);
      case "rwg_amount":
        return getAmountInCurrencyFormat(showVal(totals?.rwg?.amount));
      case "rws_weight":
        return showVal(totals?.rws?.weight);
      case "rws_rate":
        return getAmountInCurrencyFormat(showVal(totals?.rws?.rate));
      case "rws_amount":
        return getAmountInCurrencyFormat(showVal(totals?.rws?.amount));
      default:
        return "—";
    }
  };
  return (
    <tr className={rowClass}>
      {editMode && <td />}
      {visibleColumns.map((col) => (
        <td key={`rx-tot-${col.key}`} className="lt2-td lt2-td-total">
          {cell(rxTotals, col.key)}
        </td>
      ))}
      <td className="lt2-divider-col" />
      {visibleColumns.map((col) => (
        <td key={`gv-tot-${col.key}`} className="lt2-td lt2-td-total">
          {cell(gvTotals, col.key)}
        </td>
      ))}
      {editMode && <td />}
    </tr>
  );
}

/* ─────────────────────────────────────────
   BalanceRow
───────────────────────────────────────── */
function BalanceRow({ balance, visibleColumns, editMode }) {
  const cell = (colKey) => {
    switch (colKey) {
      case "serial":
        return "Balance";
      case "date":
        return "";
      case "party":
        return "";
      case "cash":
        return getAmountInCurrencyFormat(showVal(balance?.cash));
      case "pc":
        return (
          <SectionCell
            label="PC Bal"
            weight={balance?.pc?.weight}
            amount={balance?.pc?.charges}
            paidAmount={balance?.pc?.paidAmount}
          />
        );
      case "lab_weight":
        return (
          <SectionCell
            label="Lab Bal"
            weight={balance?.lab?.weight}
            totalWeight={balance?.lab?.totalWeight}
            amount={balance?.lab?.charges}
            paidAmount={balance?.lab?.paidAmount}
            labelForWeight="Pure Weight"
          />
        );
      case "lab_report":
        return "—";
      case "bar":
        return (
          <SectionCell
            label="Bar Bal"
            weight={balance?.bar?.weight}
            amount={balance?.bar?.charges}
            paidAmount={balance?.bar?.paidAmount}
          />
        );
      case "coin":
        return (
          <SectionCell
            label="Coin Bal"
            weight={balance?.coin?.weight}
            amount={balance?.coin?.charges}
            paidAmount={balance?.coin?.paidAmount}
          />
        );
      case "coin_karat":
        return "—";
      case "ginni":
        return (
          <SectionCell
            label="Ginni Bal"
            weight={balance?.ginni?.weight}
            totalWeight={balance?.ginni?.totalWeight}
            amount={balance?.ginni?.charges}
            paidAmount={balance?.ginni?.paidAmount}
            labelForWeight="Pure Weight"
          />
        );
      case "ginni_report":
        return "—";
      case "silver_weight":
        return (
          <SectionCell
            label="Silver Bal"
            weight={balance?.silver?.weight}
            amount={balance?.silver?.charges}
            paidAmount={balance?.silver?.paidAmount}
          />
        );
      case "silver_bar":
        return showVal(balance?.silver?.silverBar);
      case "silver_amount":
        return getAmountInCurrencyFormat(showVal(balance?.silver?.charges));
      case "rwg_weight":
        return showVal(balance?.rwg?.weight);
      case "rwg_rate":
        return getAmountInCurrencyFormat(showVal(balance?.rwg?.rate));
      case "rwg_amount":
        return getAmountInCurrencyFormat(showVal(balance?.rwg?.amount));
      case "rws_weight":
        return showVal(balance?.rws?.weight);
      case "rws_rate":
        return getAmountInCurrencyFormat(showVal(balance?.rws?.rate));
      case "rws_amount":
        return getAmountInCurrencyFormat(showVal(balance?.rws?.amount));
      default:
        return "—";
    }
  };
  return (
    <tr className="lt2-balance-row">
      {editMode && <td />}
      {visibleColumns.map((col) => (
        <td key={`bal-rx-${col.key}`} className="lt2-td lt2-td-balance">
          {cell(col.key)}
        </td>
      ))}
      <td className="lt2-divider-col" />
      {visibleColumns.map((col) => (
        <td key={`bal-gv-${col.key}`} className="lt2-td lt2-td-balance-gv">
          {col.key === "serial" ? "" : ""}
        </td>
      ))}
      {editMode && <td />}
    </tr>
  );
}

/* ─────────────────────────────────────────
   SectionCell
   CRITICAL: all hooks called unconditionally at the top,
   early return only AFTER all hooks.
───────────────────────────────────────── */
function SectionCell({
  weight,
  totalWeight,
  amount,
  paidAmount,
  label,
  isSummaryRow = false,
  labelForWeight = "Net Weight",
}) {
  // ── ALL HOOKS FIRST — no exceptions ──
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener("mousedown", close);
    // document.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("mousedown", close);
      // document.removeEventListener("scroll", close, true);
    };
  }, [open]);

  // ── early return AFTER hooks ──
  const hasWeight = typeof weight === "number";
  if (!hasWeight) return <span className="lt2-empty-dash">—</span>;

  const status = !isSummaryRow ? chargeStatus(amount, paidAmount) : null;

  const openPopup = (e) => {
    e.stopPropagation();
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const popupW = 230;
    let left = rect.left + rect.width / 2 - popupW / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - popupW - 8));
    const top = rect.bottom + window.scrollY + 6;
    setCoords({ top, left });
    setOpen((p) => !p);
  };

  return (
    <div className="sc-wrap">
      <button
        ref={triggerRef}
        className="sc-trigger"
        onClick={openPopup}
        title={`Click to view ${label} details`}
      >
        <span className="sc-weight">{limitNumber(weight)} g</span>
        {status && (
          <span className={`sc-badge sc-badge-${status}`}>
            {BADGE_LABELS[status]}
          </span>
        )}
      </button>

      {open &&
        createPortal(
          <div
            className="sc-popup"
            style={{
              top: coords.top,
              left: coords.left,
              width: 230,
              position: "absolute",
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="sc-popup-header">
              <span className="sc-popup-title">{label} Details</span>
              <button className="sc-popup-close" onClick={() => setOpen(false)}>
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="sc-popup-body">
              <PopupRow
                icon="fa-weight-hanging"
                label={labelForWeight}
                value={`${limitNumber(weight)} g`}
              />
              {totalWeight !== undefined &&
                totalWeight !== null &&
                totalWeight !== 0 && (
                  <PopupRow
                    icon="fa-layer-group"
                    label="Total Wt"
                    value={`${limitNumber(totalWeight)} g`}
                  />
                )}
              {amount !== undefined && (
                <>
                  <div className="sc-popup-divider" />
                  <PopupRow
                    icon="fa-file-invoice"
                    label="Charges"
                    value={
                      amount && amount !== 0
                        ? getAmountInCurrencyFormat(amount)
                        : "—"
                    }
                  />
                  {!isSummaryRow && (
                    <PopupRow
                      icon="fa-hand-holding-dollar"
                      label="Paid"
                      value={
                        paidAmount && paidAmount !== 0
                          ? getAmountInCurrencyFormat(paidAmount)
                          : "—"
                      }
                    />
                  )}
                  {!isSummaryRow && amount > 0 && (
                    <>
                      <div className="sc-popup-divider" />
                      <ChargeStatusRow
                        amount={amount}
                        paidAmount={paidAmount}
                      />
                    </>
                  )}
                </>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

/* ─────────────────────────────────────────
   Popup sub-components
───────────────────────────────────────── */
function PopupRow({ icon, label, value }) {
  return (
    <div className="sc-popup-row">
      <span className="sc-popup-row-label">
        <i className={`fas ${icon} sc-popup-row-icon`} />
        {label}
      </span>
      <span className="sc-popup-row-value">{value}</span>
    </div>
  );
}

function ChargeStatusRow({ amount, paidAmount }) {
  const paid = paidAmount || 0;
  const status = chargeStatus(amount, paid);
  const diff = Math.abs(amount - paid);
  const CONFIG = {
    paid: {
      icon: "fa-circle-check",
      label: "Fully Paid",
      note: null,
      cls: "paid",
    },
    due: {
      icon: "fa-clock",
      label: "Due",
      note: `${limitNumber(diff)} remaining`,
      cls: "due",
    },
    payback: {
      icon: "fa-rotate-left",
      label: "Payable Back",
      note: `${limitNumber(diff)} excess`,
      cls: "payback",
    },
  };
  const c = CONFIG[status] || CONFIG.due;
  return (
    <div className={`sc-popup-status sc-status-${c.cls}`}>
      <i className={`fas ${c.icon}`} />
      <span className="sc-status-label">{c.label}</span>
      {c.note && <span className="sc-status-note">{c.note}</span>}
    </div>
  );
}

/* ─────────────────────────────────────────
   Helpers
───────────────────────────────────────── */
const BADGE_LABELS = { paid: "Paid", due: "Due", payback: "↩ Back" };

const showVal = (val) => (val && val !== 0 ? limitNumber(val) : "—");

const isReportMissing = (labWeight, report) => {
  const weight = Number(labWeight);
  const reportValue = Number(report);
  if (weight > 0) {
    if (!report) return true;
    if (reportValue === 0) return true;
  }
  return false;
};
